import { Box } from '@chakra-ui/react'
import { memo, type ReactNode } from 'react'
import type { TooltipContentProps } from 'recharts'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { MIN_BAR_CHART_DOMAIN } from '../utils/reportingConstants'

interface NumberData {
  number: string
  displayNumber: string
  name: string
  inboundCalls: number
  totalSeconds: number
  averageDuration: number
}

interface ChartColors {
  xTickFill: string
  xStroke: string
  yTickFill: string
  yStroke: string
  gridStroke: string
  labelFill: string
}

interface NumberComparisonChartBatchProps {
  data: NumberData[]
  startIndex: number
  barColors: string[]
  chartColors: ChartColors
  CustomTooltip: (props: TooltipContentProps) => ReactNode
  isLast: boolean
  isFirst: boolean
  maxValue: number
}

const NumberComparisonChartBatch = memo(
  ({
    data,
    startIndex,
    barColors,
    chartColors,
    CustomTooltip,
    isLast,
    isFirst,
    maxValue,
  }: NumberComparisonChartBatchProps) => {
    const xAxisTextStyle = textStyles[textStyleKeys.Body3]
    const fontSizeString = Array.isArray(xAxisTextStyle.fontSize)
      ? xAxisTextStyle.fontSize[0]
      : xAxisTextStyle.fontSize
    const fontSize = parseFloat(fontSizeString)
    const fontFamily = xAxisTextStyle.fontFamily

    const getBarColor = (index: number): string => {
      const globalIndex = startIndex + index
      const colorIndex = globalIndex % barColors.length
      if (
        globalIndex > 0 &&
        colorIndex === (globalIndex - 1) % barColors.length
      ) {
        return barColors[(colorIndex + 1) % barColors.length]
      }
      return barColors[colorIndex]
    }

    const heightPerItem = 50
    const bottomPadding = 30
    const topPadding = isFirst ? 10 : 0
    const chartHeight = data.length * heightPerItem + topPadding + bottomPadding

    return (
      <Box
        width="100%"
        height={`${chartHeight}px`}
        mt={isFirst ? '0' : '-30px'}
        pr="60px"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{
              top: Math.max(topPadding, 0),
              right: 60,
              left: 0,
              bottom: bottomPadding,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.gridStroke}
            />
            <XAxis
              type="number"
              domain={[0, Math.max(maxValue, MIN_BAR_CHART_DOMAIN)]}
              stroke={chartColors.xStroke}
              tick={{
                fill: chartColors.xTickFill,
                fontFamily: fontFamily,
                style: { fontSize: `${fontSize}px` },
              }}
              tickFormatter={(value) => value.toLocaleString()}
              hide={!isLast}
              interval="preserveStartEnd"
            />
            <YAxis
              type="category"
              dataKey="displayNumber"
              stroke={chartColors.yStroke}
              tick={{
                fill: chartColors.yTickFill,
                fontFamily: fontFamily,
                style: { fontSize: `${fontSize}px` },
                textAnchor: 'start',
                dx: -110,
              }}
              width={180}
              interval={0}
            />
            <Tooltip content={CustomTooltip} cursor={{ fill: '#aaaaaa33' }} />
            <Bar dataKey="inboundCalls" radius={[0, 4, 4, 0]} barSize={38}>
              {data.map((entry, index) => (
                <Cell key={`cell-${entry.number}`} fill={getBarColor(index)} />
              ))}
              <LabelList
                dataKey="inboundCalls"
                position="right"
                formatter={(value) =>
                  typeof value === 'number'
                    ? value.toLocaleString()
                    : String(value ?? '')
                }
                style={{
                  fill: chartColors.labelFill,
                  fontFamily,
                  fontSize: `${fontSize}px`,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.maxValue === nextProps.maxValue &&
      prevProps.startIndex === nextProps.startIndex &&
      prevProps.isFirst === nextProps.isFirst &&
      prevProps.isLast === nextProps.isLast &&
      prevProps.chartColors === nextProps.chartColors
    )
  },
)

NumberComparisonChartBatch.displayName = 'NumberComparisonChartBatch'

export default NumberComparisonChartBatch
